import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {Observable} from 'rxjs';

import * as PL2 from '@common/utils/dist/models/models-pl2.js';

import {Action} from '@actions/action';
import {
  CreateSectionAction,
  SectionTarget,
} from '@actions/create-section.action';

export type AddQuestionSectionInput =
  | {
      type: 'multiple-choice';
      title: string;
      prompt: string;
      options: string[];
      correct: number[];
      multiSelect: boolean;
      hints?: string[];
    }
  | {type: 'free-response'; title: string; prompt: string; hint?: string};

/**
 * The question section entry for a question card, as the question section directive
 * creates them: multiple choice has opts (and cA), free response doesn't.
 */
export function questionSectionEntry(
  input: AddQuestionSectionInput,
  defaultName: string,
): Partial<PL2.QuestionSectionEntry> {
  const n = input.title || defaultName;
  if (input.type === 'free-response') {
    // Free response sections have no hint field, so the hint is part of the question
    return {
      n,
      qs: input.hint ? `${input.prompt}\n\nHint: ${input.hint}` : input.prompt,
    };
  }
  return {
    n,
    qs: input.prompt,
    opts: input.options,
    cA: input.multiSelect ? input.correct : input.correct[0],
    mS: input.multiSelect,
    ...(input.hints ? {hts: input.hints, sH: true} : {}),
  };
}

/** Adds a question card proposed by the lesson chat to the end of the current lesson. */
@Injectable({providedIn: 'root'})
export class AddQuestionSectionToLessonAction implements Action<
  AddQuestionSectionInput,
  PL2.AnySectionEntry
> {
  private _createSectionAction = inject(CreateSectionAction);
  private _translate = inject(TranslateService);

  execute(
    input: AddQuestionSectionInput,
    target?: SectionTarget,
  ): Observable<PL2.AnySectionEntry> {
    const defaultName = this._translate.instant(
      input.type === 'multiple-choice'
        ? 'question-section.nDefaultMultipleChoice'
        : 'question-section.nDefaultFreeResponse',
    );
    return this._createSectionAction.execute({
      partialEntry: questionSectionEntry(input, defaultName),
      entryType: PL2.EntryType.QuestionSection,
      index: Number.MAX_SAFE_INTEGER,
      scope: PL2.SectionScope.Individual,
      target,
    });
  }
}
